import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { mondayOf, weekDays, isoDate, addDays, DAY_NAMES_SHORT, DAY_NAMES, fmtNL } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, ChevronRight, Send, Trash2, X, Truck, User as UserIcon, Users2, Clock, Camera, Copy, RefreshCcw, CalendarDays, Filter, Search } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PlanbordSearch } from "@/components/PlanbordSearch";
import { WeekNote } from "@/components/WeekNote";
import { StaleProjects } from "@/components/StaleProjects";
import { getPalette, paletteName, freeSlot } from "@/lib/palette";
import { nl } from "date-fns/locale";

const STATUS_LABEL = { gepland: "Gepland", bevestigd: "Bevestigd", gestart: "Bezig", afgerond: "Afgerond" };

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export default function Planbord() {
  const { user, company } = useAuth();
  const dayStart = company?.settings?.workday_start || "07:00";
  const dayEnd = company?.settings?.workday_end || "16:00";
  const [weekStart, setWeekStartRaw] = useState(() => {
    try {
      const s = sessionStorage.getItem("pb_planbord_week");
      if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split("-").map(Number);
        return new Date(y, m - 1, d);
      }
    } catch (err) { console.warn("Week herstellen mislukt", err); }
    return mondayOf(new Date());
  });
  const setWeekStart = useCallback((d) => {
    setWeekStartRaw(d);
    try { sessionStorage.setItem("pb_planbord_week", isoDate(d)); } catch (err) { console.warn("Week opslaan mislukt", err); }
  }, []);
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const weekReqRef = useRef(0);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("all"); // 'all' | 'unplanned' | 'color:#RRGGBB'
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showConflicts, setShowConflicts] = useState(false);
  const [savingRefresh, setSavingRefresh] = useState(false);
  const [naloopRefresh, setNaloopRefresh] = useState(0);
  // Rechtermuis-klik menu om snel projecten toe te voegen aan een cel.
  const [addMenu, setAddMenu] = useState(null); // { userId, dateIso, x, y, start?, end? }
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const palette = useMemo(() => getPalette(company), [company]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
      else if (e.key === "/" && !typing) { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!highlightId) return undefined;
    const el = document.querySelector(`[data-assignment-id="${highlightId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [highlightId, assignments]);

  const days = useMemo(() => weekDays(weekStart).slice(0, 5), [weekStart]);
  const from = isoDate(days[0]);
  const to = isoDate(days[days.length - 1]);
  const todayIso = isoDate(new Date());

  // Prefetch-cache voor week-navigatie: { "YYYY-MM-DD_YYYY-MM-DD": {assignments, conflicts, at} }
  // Wordt gevuld op hover van Vorige/Volgende zodat de klik zelf instant voelt.
  const weekCacheRef = React.useRef(new Map());

  // Static data (users/projects/vehicles/teams/materials) — verandert zelden en NIET
  // per week. Laad éénmalig op mount + een refresh knop. Voorheen refetche elke
  // week-nav 7 endpoints i.p.v. 2, wat ~5x latency toevoegde bij week-navigatie.
  const loadStatic = useCallback(async () => {
    const [u, p, v, t, m] = await Promise.all([
      api.get("/users"),
      api.get("/projects", { params: { lite: 1 } }),
      api.get("/vehicles"),
      api.get("/teams"),
      api.get("/materials"),
    ]);
    setUsers(u.data.filter((x) => x.active !== false));
    setProjects(p.data);
    setVehicles(v.data);
    setTeams(t.data);
    setMaterials(m.data);
  }, []);

  // Week-scoped data: alleen assignments + conflicts per week-navigatie.
  // Kijkt eerst in de prefetch-cache (hover-warmup) — bij hit is de klik-response instant.
  const loadWeek = useCallback(async () => {
    const key = `${from}_${to}`;
    const reqId = ++weekReqRef.current;
    const cached = weekCacheRef.current.get(key);
    // Cache maximaal 30s vers houden om stale data te voorkomen na een save elders
    if (cached && Date.now() - cached.at < 30000) {
      setAssignments(cached.assignments);
      setConflicts(cached.conflicts);
      return;
    }
    const [a, c] = await Promise.all([
      api.get("/assignments", { params: { from_date: from, to_date: to } }),
      api.get("/conflicts", { params: { from_date: from, to_date: to } }),
    ]);
    weekCacheRef.current.set(key, { assignments: a.data, conflicts: c.data, at: Date.now() });
    if (reqId !== weekReqRef.current) return;
    setAssignments(a.data);
    setConflicts(c.data);
  }, [from, to]);

  // Prefetch: fire-and-forget week ophalen zonder state-updates te triggeren.
  const prefetchWeek = useCallback(async (offsetDays) => {
    try {
      const targetMonday = addDays(weekStart, offsetDays);
      const targetDays = weekDays(targetMonday).slice(0, 5);
      const pFrom = isoDate(targetDays[0]);
      const pTo = isoDate(targetDays[targetDays.length - 1]);
      const key = `${pFrom}_${pTo}`;
      const cached = weekCacheRef.current.get(key);
      if (cached && Date.now() - cached.at < 30000) return; // al vers
      const [a, c] = await Promise.all([
        api.get("/assignments", { params: { from_date: pFrom, to_date: pTo } }),
        api.get("/conflicts", { params: { from_date: pFrom, to_date: pTo } }),
      ]);
      weekCacheRef.current.set(key, { assignments: a.data, conflicts: c.data, at: Date.now() });
    } catch (_) { /* prefetch is best-effort */ }
  }, [weekStart]);

  // Combined refresher: gebruikt in child callbacks die "load()" verwachten.
  // Cache-bust nodig zodat na een save de gebruiker een verse view krijgt.
  const load = useCallback(async () => {
    weekCacheRef.current.clear();
    await Promise.all([loadStatic(), loadWeek()]);
  }, [loadStatic, loadWeek]);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { loadWeek(); }, [loadWeek]);

  const workUsers = useMemo(() => {
    const all = users.filter((u) => u.role !== "OWNER" && u.role !== "PLANNER");
    return (all.length > 0 ? all : users);
  }, [users]);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const projectMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const vehicleMap = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v])), [vehicles]);

  // Expand user_id into user_id + all teammates
  const expandTeam = useCallback((userId) => {
    const set = new Set([userId]);
    const u = userMap[userId];
    if (u?.team_id) {
      users.forEach((x) => { if (x.team_id === u.team_id && x.active !== false) set.add(x.id); });
    }
    return [...set];
  }, [userMap, users]);

  const conflictSet = useMemo(() => {
    const s = new Set();
    conflicts.forEach((c) => {
      (c.assignment_ids || (c.assignment_id ? [c.assignment_id] : [])).forEach((id) => s.add(id));
    });
    return s;
  }, [conflicts]);

  const cellIndex = useMemo(() => {
    const idx = new Map();
    for (const a of assignments) {
      for (const uid of new Set((a.members || []).map((m) => m.user_id))) {
        const k = `${uid}|${a.date}`;
        if (!idx.has(k)) idx.set(k, []);
        idx.get(k).push(a);
      }
    }
    for (const list of idx.values()) {
      list.sort((x, y) => (x.start_time || "").localeCompare(y.start_time || "") || (x.end_time || "").localeCompare(y.end_time || ""));
    }
    return idx;
  }, [assignments]);
  const cellAssignments = (userId, dateIso) => cellIndex.get(`${userId}|${dateIso}`) || [];

  const onDragOver = (e) => { e.preventDefault(); e.currentTarget.classList.add("pb-drop-active"); };
  const onDragLeave = (e) => e.currentTarget.classList.remove("pb-drop-active");

  const onDrop = async (e, userId, dateIso, slot = null) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove("pb-drop-active");
    e.currentTarget.closest(".pb-planbord-cell")?.classList.remove("pb-drop-active");
    const data = e.dataTransfer.getData("application/json");
    if (!data) return;
    const payload = JSON.parse(data);
    const teamUserIds = expandTeam(userId);
    // Auto-suggest non-overlapping times if this user already has assignments that day
    const existingSameDay = assignments.filter((a) => a.date === dateIso && teamUserIds.some((uid) => (a.members || []).some((m) => m.user_id === uid)));
    let defaultStart = dayStart;
    let defaultEnd = dayEnd;
    if (existingSameDay.length > 0) {
      const toMins = (t) => { const [h,m] = (t||"00:00").split(":").map(Number); return h*60+m; };
      const fmt = (mins) => `${String(Math.floor(mins/60)).padStart(2,"0")}:${String(mins%60).padStart(2,"0")}`;
      const latestEnd = existingSameDay.reduce((max, a) => Math.max(max, toMins(a.end_time || "16:00")), 0);
      const startMin = Math.min(latestEnd + 30, 20*60);
      const endMin = Math.min(startMin + 3*60, 22*60);
      defaultStart = fmt(startMin);
      defaultEnd = fmt(endMin);
    }
    if (slot) { defaultStart = slot.start; defaultEnd = slot.end; }
    try {
      if (payload.type === "project") {
        const existing = assignments.find((a) => a.date === dateIso && a.project_id === payload.id);
        if (existing) {
          const existingIds = new Set((existing.members || []).map((m) => m.user_id));
          const additions = teamUserIds.filter((uid) => !existingIds.has(uid));
          if (additions.length === 0) return;
          const hasDriver = (existing.members || []).some((m) => m.role_today === "chauffeur");
          const newMembers = [
            ...(existing.members || []),
            ...additions.map((uid, i) => ({ user_id: uid, role_today: (!hasDriver && i === 0) ? "chauffeur" : "meerijder" })),
          ];
          await api.patch(`/assignments/${existing.id}`, { members: newMembers });
        } else {
          const members = teamUserIds.map((uid, i) => ({ user_id: uid, role_today: i === 0 ? "chauffeur" : "meerijder" }));
          await api.post("/assignments", {
            date: dateIso, project_id: payload.id,
            start_time: defaultStart, end_time: defaultEnd,
            members, status: "gepland",
          });
        }
      } else if (payload.type === "assignment") {
        const source = assignments.find((a) => a.id === payload.assignmentId);
        if (!source) return;
        if (source.date === dateIso && (source.members || []).some((m) => m.user_id === userId)) return;
        // remove sourceUser from source
        const remaining = (source.members || []).filter((m) => m.user_id !== payload.userId);
        if (source.date !== dateIso) {
          if (remaining.length > 0) {
            await api.patch(`/assignments/${source.id}`, { members: remaining });
          } else {
            await api.delete(`/assignments/${source.id}`);
          }
          const targetExisting = assignments.find((a) => a.date === dateIso && a.project_id === source.project_id);
          if (targetExisting) {
            const ex = new Set((targetExisting.members || []).map((m) => m.user_id));
            const add = teamUserIds.filter((uid) => !ex.has(uid)).map((uid) => ({ user_id: uid, role_today: "meerijder" }));
            if (add.length) await api.patch(`/assignments/${targetExisting.id}`, { members: [...(targetExisting.members || []), ...add] });
          } else {
            await api.post("/assignments", {
              date: dateIso, project_id: source.project_id,
              vehicle_id: source.vehicle_id, start_time: source.start_time, end_time: source.end_time,
              members: teamUserIds.map((uid, i) => ({ user_id: uid, role_today: i === 0 ? "chauffeur" : "meerijder" })),
              status: "gepland",
            });
          }
        } else {
          const ex = new Set(remaining.map((m) => m.user_id));
          const add = teamUserIds.filter((uid) => !ex.has(uid)).map((uid) => ({ user_id: uid, role_today: "meerijder" }));
          await api.patch(`/assignments/${source.id}`, { members: [...remaining, ...add] });
        }
      }
      setSavingRefresh(true);
      await load();
      const dd = new Date(`${dateIso}T12:00:00`);
      toast.success(`Ingepland op ${DAY_NAMES[(dd.getDay() + 6) % 7].toLowerCase()} ${fmtNL(dd)}`);
    } catch {
      toast.error("Kon planning niet bijwerken");
    } finally {
      setSavingRefresh(false);
    }
  };

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishNotify, setPublishNotify] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/assignments/publish", { from_date: from, to_date: to, notify: publishNotify });
      if (publishNotify && data.notified > 0) {
        toast.success(`${data.published} planningen gepubliceerd — ${data.notified} uitvoerders gemaild`);
      } else if (data.published > 0) {
        toast.success(`${data.published} planningen gepubliceerd (stil — geen mails verstuurd)`);
      } else {
        toast.success("Alles was al gepubliceerd");
      }
      setShowPublishModal(false);
      await load();
    } catch { toast.error("Publiceren mislukt"); }
    finally { setPublishing(false); }
  };

  // Set van project_id's die IN de huidige week ingepland zijn — gebruikt door filter 'Ongepland'.
  const scheduledProjectIds = useMemo(() => {
    const s = new Set();
    assignments.forEach((a) => { if (a.project_id) s.add(a.project_id); });
    return s;
  }, [assignments]);

  // Unieke kleuren uit de projecten-lijst voor de "Filter op kleur" dropdown.
  const projectColors = useMemo(() => {
    const s = new Set();
    projects.forEach((p) => { if (p.color) s.add(p.color); });
    return Array.from(s);
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    return projects
      .filter((p) => p.status !== "afgerond")
      .filter((p) => {
        if (!q) return true;
        return (
          (p.name || "").toLowerCase().includes(q)
          || (p.city || "").toLowerCase().includes(q)
          || (p.address || "").toLowerCase().includes(q)
          || (p.project_number || "").toLowerCase().includes(q)
        );
      })
      .filter((p) => {
        if (projectFilter === "all") return true;
        if (projectFilter === "unplanned") return !scheduledProjectIds.has(p.id);
        if (projectFilter.startsWith("color:")) return p.color === projectFilter.slice(6);
        return true;
      });
  }, [projects, projectSearch, projectFilter, scheduledProjectIds]);

  // Group users by team for sectioned display
  const teamGroups = useMemo(() => {
    const groups = [];
    const byTeam = {};
    workUsers.forEach((u) => {
      const key = u.team_id || "__none__";
      if (!byTeam[key]) byTeam[key] = [];
      byTeam[key].push(u);
    });
    teams.forEach((t) => {
      if (byTeam[t.id]?.length) groups.push({ id: t.id, name: t.name, users: byTeam[t.id] });
    });
    if (byTeam.__none__?.length) groups.push({ id: "__none__", name: teams.length ? "Zonder team" : "Uitvoerders", users: byTeam.__none__ });
    return groups;
  }, [workUsers, teams]);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="border-b-2 border-slate-900 bg-white px-6 py-4 flex flex-wrap items-center gap-3">
        <h1 className="pb-heading text-3xl font-black">PLANBORD</h1>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <button className="pb-btn-outline !min-h-[44px] flex items-center gap-1" onClick={() => setSearchOpen(true)} title="Zoek klus (Ctrl+K)" data-testid="btn-search-klus">
            <Search size={16}/> Zoek klus
          </button>
          <button className="pb-btn-outline !min-h-[44px] flex items-center gap-1" onClick={() => setWeekStart(addDays(weekStart, -7))} onMouseEnter={() => prefetchWeek(-7)} onFocus={() => prefetchWeek(-7)} data-testid="week-prev">
            <ChevronLeft size={18}/> Vorige
          </button>
          <Popover open={weekPickerOpen} onOpenChange={setWeekPickerOpen}>
            <PopoverTrigger asChild>
              <button className="pb-heading text-lg font-bold px-3 flex items-center gap-2 rounded hover:bg-slate-100 min-h-[44px]" title="Kies een week" data-testid="week-label">
                <CalendarDays size={18}/> Week {getWeekNumber(days[0])} · {fmtNL(days[0])} – {fmtNL(days[days.length - 1])}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center" data-testid="week-picker">
              <Calendar
                mode="single"
                locale={nl}
                weekStartsOn={1}
                showWeekNumber
                selected={days[0]}
                defaultMonth={days[0]}
                onSelect={(d) => {
                  if (!d) return;
                  setWeekStart(addDays(d, -((d.getDay() + 6) % 7)));
                  setWeekPickerOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
          <button className="pb-btn-outline !min-h-[44px] flex items-center gap-1" onClick={() => setWeekStart(addDays(weekStart, 7))} onMouseEnter={() => prefetchWeek(7)} onFocus={() => prefetchWeek(7)} data-testid="week-next">
            Volgende <ChevronRight size={18}/>
          </button>
          <button className="pb-btn-outline !min-h-[44px]" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="week-today">Deze week</button>
          <button
            className={`!min-h-[44px] flex items-center gap-1 px-4 rounded font-bold border-2 border-slate-900 ${conflicts.length > 0 ? "bg-red-600 text-white" : "bg-white text-slate-900"}`}
            onClick={() => setShowConflicts((v) => !v)}
            data-testid="btn-conflicts"
          >
            <AlertTriangle size={16}/> {conflicts.length} conflicten
          </button>
          <button className="pb-btn-primary !min-h-[44px] flex items-center gap-1" onClick={() => { setPublishNotify(false); setShowPublishModal(true); }} data-testid="btn-publish">
            <Send size={16}/> Publiceer week
          </button>
        </div>
      </div>

      {showConflicts && (
        <div className="bg-red-50 border-b-2 border-red-600 px-6 py-3">
          <div className="pb-heading font-bold text-red-700 mb-2">CONFLICTEN</div>
          {conflicts.length === 0 ? <div className="text-sm font-semibold text-slate-700">Geen conflicten</div> : (
            <ul className="space-y-1 text-sm font-semibold text-slate-800">
              {conflicts.map((c, i) => (
                <li key={`${c.type}-${c.date || ''}-${c.user_id || ''}-${c.vehicle_id || ''}-${i}`}>
                  <span className="inline-block px-2 py-1 bg-red-600 text-white text-xs rounded font-bold uppercase mr-2">{c.type.replace(/_/g," ")}</span>
                  {c.date && <span>{fmtNL(c.date)}</span>}{c.user_id && <span> — {userMap[c.user_id]?.name}</span>}{c.vehicle_id && <span> — {vehicleMap[c.vehicle_id]?.name}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 p-4">
        {/* Project sidebar */}
        <aside className="w-full md:w-52 shrink-0 space-y-2">
          <div className="pb-card p-2">
            {/* Sticky zoek + filter — blijven zichtbaar bij scroll door lange lijst (50+ projecten) */}
            <div className="sticky top-0 z-10 bg-white pb-2 -mx-2 px-2 border-b-2 border-slate-100">
              <div className="pb-heading text-[11px] font-bold uppercase tracking-widest mb-2 flex items-center justify-between gap-1">
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-orange-500 rounded-sm"/> Projecten <span className="text-slate-400 font-medium">({filteredProjects.length})</span></span>
                <button
                  type="button"
                  onClick={() => setShowFilterMenu((v) => !v)}
                  className={`p-1 rounded border ${projectFilter === "all" ? "border-slate-200 text-slate-500 hover:text-slate-900" : "border-orange-500 text-orange-600 bg-orange-50"}`}
                  title="Filter"
                  data-testid="planbord-filter-toggle"
                >
                  <Filter size={12}/>
                </button>
              </div>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                <input
                  className="pb-input !min-h-[32px] !text-sm !py-1 !pl-6"
                  placeholder="Zoek naam of adres…"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  data-testid="planbord-project-search"
                />
              </div>
              {showFilterMenu && (
                <div className="mt-2 border-2 border-slate-900 rounded bg-white p-2 space-y-1" data-testid="planbord-filter-menu">
                  <button
                    type="button"
                    onClick={() => { setProjectFilter("all"); setShowFilterMenu(false); }}
                    className={`w-full text-left text-xs font-black uppercase tracking-widest px-2 py-1 rounded ${projectFilter === "all" ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}
                    data-testid="filter-all"
                  >Toon alles</button>
                  <button
                    type="button"
                    onClick={() => { setProjectFilter("unplanned"); setShowFilterMenu(false); }}
                    className={`w-full text-left text-xs font-black uppercase tracking-widest px-2 py-1 rounded ${projectFilter === "unplanned" ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}
                    data-testid="filter-unplanned"
                  >Alleen ongepland (deze week)</button>
                  {projectColors.length > 0 && (
                    <div className="pt-1 border-t border-slate-200">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-2 pt-1 pb-1">Filter op kleur</div>
                      <div className="flex flex-wrap gap-1 px-1">
                        {projectColors.map((c) => (
                          <button
                            type="button"
                            key={c}
                            onClick={() => { setProjectFilter(`color:${c}`); setShowFilterMenu(false); }}
                            className={`w-6 h-6 rounded border-2 ${projectFilter === `color:${c}` ? "border-slate-900 ring-2 ring-orange-400" : "border-slate-300 hover:border-slate-900"}`}
                            style={{ background: c }}
                            title={paletteName(palette, c) || c}
                            data-testid={`filter-color-${c.replace("#","")}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {projectFilter !== "all" && (
                <button
                  type="button"
                  onClick={() => setProjectFilter("all")}
                  className="mt-2 w-full text-[10px] font-black uppercase tracking-widest text-orange-700 hover:text-orange-900 flex items-center justify-center gap-1"
                  data-testid="filter-clear"
                >
                  <X size={10}/> Filter wissen
                </button>
              )}
            </div>

            <div className="mt-2 space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto pb-scrollbar">
              {filteredProjects.map((p) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("application/json", JSON.stringify({ type: "project", id: p.id }))}
                  className="pb-block cursor-grab select-none !py-1 !px-2"
                  style={{ background: p.color || "#3B82F6" }}
                  data-testid={`sidebar-project-${p.id}`}
                  title={[p.name, p.city].filter(Boolean).join(" · ")}
                >
                  <div className="text-[12px] font-black leading-tight truncate">{p.name}</div>
                </div>
              ))}
              {filteredProjects.length === 0 && (
                <div className="text-center py-6 text-slate-500" data-testid="planbord-projects-empty">
                  <div className="text-3xl mb-1">🔍</div>
                  <div className="text-xs font-bold">Geen projecten gevonden</div>
                  {(projectSearch || projectFilter !== "all") && (
                    <button
                      type="button"
                      onClick={() => { setProjectSearch(""); setProjectFilter("all"); }}
                      className="mt-2 text-[10px] font-black uppercase tracking-widest text-orange-600 hover:text-orange-800"
                      data-testid="planbord-projects-reset"
                    >Wis filters</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {teams.length > 0 && (
            <div className="pb-card p-2">
              <div className="pb-heading text-[11px] font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                <Users2 size={12}/> Teams
              </div>
              <ul className="space-y-0.5 text-[11px] font-bold text-slate-800">
                {teams.map((t) => (
                  <li key={t.id} className="flex items-center gap-1 truncate">
                    <span className="inline-block w-2 h-2 rounded-sm border border-slate-900" style={{ background: "#F97316" }}/>
                    <span className="truncate">{t.name}</span>
                    <span className="text-slate-500 font-medium">({t.member_ids?.length || 0})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Naloop-werk sidebar section */}
          <NaloopSidebar
            refreshKey={naloopRefresh}
            onReschedule={async (naloopId, targetDate) => {
              try {
                await api.post(`/assignments/${naloopId}/schedule-naloop`, { date: targetDate });
                toast.success("Naloop ingepland");
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Kon niet inplannen");
              }
            }}
          />
        </aside>

        {/* Grid */}
        <div className="flex-1 overflow-x-auto pb-scrollbar">
          <StaleProjects onDeleted={() => { weekCacheRef.current.clear(); load(); }} />
          <WeekNote weekStart={from} canEdit={["OWNER", "PLANNER"].includes(user?.role)} />
          <div className="pb-planbord-grid min-w-[720px]">
            <div className="pb-planbord-header pb-planbord-header-emp">UITVOERDERS</div>
            {days.map((d, i) => {
              const iso = isoDate(d);
              const isToday = iso === todayIso;
              return (
                <div key={`hd-${iso}`} className={`pb-planbord-header ${isToday ? "pb-planbord-header-today" : ""}`}>
                  <div className="text-[11px] tracking-widest opacity-90">{DAY_NAMES_SHORT[i]}</div>
                  <div className="pb-heading text-lg font-black">{fmtNL(d).slice(0, 5)}</div>
                </div>
              );
            })}
            {teamGroups.map((g) => (
              <React.Fragment key={g.id}>
                <div className="pb-planbord-team-header">{g.name}</div>
                {days.map((_, i) => <div key={`th-${g.id}-${i}`} className="pb-planbord-team-fill" />)}
                {g.users.map((u) => (
                  <React.Fragment key={u.id}>
                    <div className="pb-planbord-emp">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-black">
                          {initials(u.name)}
                        </div>
                        <div>
                          <div className="text-sm font-black leading-tight">{u.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{u.function || u.role}</div>
                        </div>
                      </div>
                    </div>
                    {days.map((d) => {
                      const di = isoDate(d);
                      const items = cellAssignments(u.id, di);
                      const isToday = di === todayIso;
                      const slot = freeSlot(items, dayStart, dayEnd);
                      const slotEl = slot && (
                        <button
                          type="button"
                          className="pb-free-slot"
                          onClick={(e) => setAddMenu({ userId: u.id, dateIso: di, x: e.clientX, y: e.clientY, start: slot.start, end: slot.end })}
                          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setAddMenu({ userId: u.id, dateIso: di, x: e.clientX, y: e.clientY, start: slot.start, end: slot.end }); }}
                          onDragOver={(e) => { e.stopPropagation(); onDragOver(e); }}
                          onDragLeave={onDragLeave}
                          onDrop={(e) => onDrop(e, u.id, di, slot)}
                          title="Klik of sleep een project hierheen"
                          data-testid={`free-slot-${u.id}-${di}`}
                        >
                          {slot.label} <span className="font-semibold">{slot.start}–{slot.end}</span>
                        </button>
                      );
                      return (
                        <div
                          key={di}
                          className={`pb-planbord-cell ${isToday ? "pb-planbord-cell-today" : ""}`}
                          onDragOver={onDragOver}
                          onDragLeave={onDragLeave}
                          onDrop={(e) => onDrop(e, u.id, di)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setAddMenu({ userId: u.id, dateIso: di, x: e.clientX, y: e.clientY });
                          }}
                          data-testid={`planbord-cell-${u.id}-${di}`}
                        >
                          {slot?.pos === "before" && slotEl}
                          {items.map((a) => {
                            const proj = projectMap[a.project_id];
                            const veh = vehicleMap[a.vehicle_id];
                            const isConflict = conflictSet.has(a.id);
                            const me = (a.members || []).find((m) => m.user_id === u.id);
                            return (
                              <div
                                key={a.id}
                                className={`pb-block ${highlightId === a.id ? "pb-block-flash" : ""}`}
                                data-assignment-id={a.id}
                                style={{ background: proj?.color || "#3B82F6" }}
                                draggable
                                onDragStart={(e) => e.dataTransfer.setData("application/json", JSON.stringify({ type: "assignment", assignmentId: a.id, userId: u.id }))}
                                onClick={() => setSelectedAssignment(a)}
                                data-conflict={isConflict}
                                data-testid={`planbord-block-${a.id}-${u.id}`}
                              >
                                <div className="text-[13px] font-black leading-tight">{proj?.name || "?"}</div>
                                <div className="text-[11px] opacity-95 font-semibold flex items-center gap-1">
                                  <Clock size={11}/> {a.start_time}–{a.end_time}
                                </div>
                                {veh && (
                                  <div className="text-[11px] opacity-95 font-semibold flex items-center gap-1">
                                    <Truck size={11}/> {veh.name.replace(/^Bus /,"B")} {me?.role_today === "chauffeur" && <span className="bg-white/30 px-1 rounded text-[9px]">RIJDER</span>}
                                  </div>
                                )}
                                <div className="flex items-center justify-between mt-1">
                                  <div className="flex -space-x-1">
                                    {(a.members || []).slice(0, 4).map((m) => (
                                      <div key={m.user_id} title={userMap[m.user_id]?.name} className="w-4 h-4 rounded-full bg-white/90 flex items-center justify-center text-[8px] font-black border border-slate-900" style={{ color: "#0F172A" }}>
                                        {initials(userMap[m.user_id]?.name || "?")}
                                      </div>
                                    ))}
                                  </div>
                                  {!a.published && <div className="text-[9px] uppercase font-black bg-white/30 px-1 rounded">Concept</div>}
                                </div>
                              </div>
                            );
                          })}
                          {slot?.pos === "after" && slotEl}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-600 font-semibold flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap" data-testid="planbord-legend">
              {palette.filter((p) => p.name).map((p) => (
                <div key={p.color} className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm border border-slate-900" style={{ background: p.color }}/> {p.name}</div>
              ))}
              <div className="flex items-center gap-1"><span className="w-3 h-3 border-2 border-red-600 rounded-sm"/> Conflict</div>
              <div className="flex items-center gap-1"><span className="pb-free-slot-swatch"/> Vrij dagdeel</div>
            </div>
            <div className="ml-auto">Tip: sleep een project op één teamlid — <b>de héle team</b> wordt meegenomen.</div>
          </div>
        </div>
      </div>

      {selectedAssignment && (
        <AssignmentDrawer
          key={selectedAssignment.id}
          assignment={selectedAssignment}
          onClose={() => setSelectedAssignment(null)}
          onSaved={async (opts) => {
            if (!(opts && opts.keepOpen)) {
              setSelectedAssignment(null);
            }
            setNaloopRefresh((v) => v + 1);
            await load();
          }}
          projects={projects}
          vehicles={vehicles}
          users={users}
          materials={materials}
        />
      )}

      <PlanbordSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPick={(r) => {
          const [y, m, d] = r.date.split("-").map(Number);
          const dt = new Date(y, m - 1, d);
          setSearchOpen(false);
          setWeekStart(addDays(dt, -((dt.getDay() + 6) % 7)));
          setHighlightId(r.id);
          if (dt.getDay() === 0 || dt.getDay() === 6) toast.info("Deze klus staat in het weekend en valt buiten het planbord.");
        }}
      />

      {addMenu && (
        <AddProjectMenu
          anchor={addMenu}
          projects={filteredProjects}
          onClose={() => setAddMenu(null)}
          onPick={async (projectId) => {
            const tgt = addMenu;
            setAddMenu(null);
            try {
              const teamUserIds = expandTeam(tgt.userId);
              const members = teamUserIds.map((uid, i) => ({ user_id: uid, role_today: i === 0 ? "chauffeur" : "meerijder" }));
              await api.post("/assignments", {
                date: tgt.dateIso, project_id: projectId,
                start_time: tgt.start || dayStart, end_time: tgt.end || dayEnd,
                members, status: "gepland",
              });
              toast.success("Project toegevoegd");
              await load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Kon niet toevoegen"); }
          }}
          onImportNotion={() => { setAddMenu(null); window.location.href = "/projecten?notion=1"; }}
        />
      )}

      {showPublishModal && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-center justify-center p-4">
          <div className="pb-card w-full max-w-md p-6" data-testid="publish-modal">
            <div className="pb-heading text-xl font-black mb-1">Publiceer week</div>
            <div className="text-sm font-semibold text-slate-700 mb-4">
              Alle concept-planningen van <b>{fmtNL(days[0])}</b> t/m <b>{fmtNL(days[days.length - 1])}</b> worden zichtbaar voor de uitvoerders op hun Vandaag-scherm.
            </div>
            <label className="flex items-start gap-3 p-3 border-2 border-slate-200 rounded cursor-pointer hover:border-orange-600 has-[:checked]:border-orange-600 has-[:checked]:bg-orange-50" data-testid="publish-notify-label">
              <input
                type="checkbox"
                checked={publishNotify}
                onChange={(e) => setPublishNotify(e.target.checked)}
                className="mt-1 w-5 h-5 accent-orange-600"
                data-testid="publish-notify-checkbox"
              />
              <div>
                <div className="font-black text-sm text-slate-900">Uitvoerders per e-mail informeren</div>
                <div className="text-xs font-medium text-slate-600 mt-0.5">
                  Aan als je een nieuwe week publiceert. Uit voor kleine correcties — dan blijven ze niet in mail-notificaties zitten.
                </div>
              </div>
            </label>
            <div className="flex gap-2 mt-5">
              <button className="pb-btn-outline flex-1" onClick={() => setShowPublishModal(false)} data-testid="publish-cancel" disabled={publishing}>Annuleren</button>
              <button className="pb-btn-primary flex-1 flex items-center justify-center gap-1" onClick={publish} data-testid="publish-confirm" disabled={publishing}>
                <Send size={16}/> {publishing ? "Bezig…" : "Publiceren"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NaloopSidebar({ onReschedule, refreshKey }) {
  const [items, setItems] = useState([]);
  const [pickerFor, setPickerFor] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/assignments/naloop");
      setItems(r.data || []);
    } catch (err) { console.warn("Naloop laden mislukt", err); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { load(); }, [refreshKey, load]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const fmtNlShort = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}`;
  };
  const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (items.length === 0) return null;
  return (
    <div className="pb-card p-2" data-testid="naloop-sidebar">
      <div className="pb-heading text-[11px] font-bold uppercase tracking-widest mb-2 flex items-center gap-1 text-orange-700">
        <RefreshCcw size={12}/> Naloop ({items.length})
      </div>
      <ul className="space-y-1">
        {items.map((n) => (
          <li key={n.id} className="border-2 border-orange-500 rounded p-1.5 bg-orange-50" data-testid={`naloop-item-${n.id}`}>
            <div className="text-[11px] font-black truncate" style={{ color: n.project_color }}>{n.project_name || "?"}</div>
            <div className="text-[10px] text-slate-600 font-semibold truncate">Van {n.date} · {n.members?.length || 0} man</div>
            {n.note && <div className="text-[10px] text-slate-700 italic truncate">{n.note}</div>}
            {pickerFor === n.id ? (
              <Popover open={true} onOpenChange={(o) => { if (!o) { setPickerFor(null); setSelected(null); } }}>
                <PopoverTrigger asChild>
                  <button className="mt-1 w-full flex items-center justify-between gap-1 pb-input !min-h-[30px] !text-[11px] !py-0.5 !px-1" data-testid={`naloop-date-${n.id}`}>
                    <span className="flex items-center gap-1"><CalendarDays size={11}/> {selected ? fmtNlShort(selected) : "Kies datum"}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[60]" align="start">
                  <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={async (d) => {
                      if (!d) return;
                      setSelected(d);
                      await onReschedule(n.id, toIso(d));
                      setPickerFor(null); setSelected(null); await load();
                    }}
                    disabled={{ before: today }}
                    weekStartsOn={1}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <button
                className="mt-1 w-full text-[11px] font-bold uppercase tracking-widest text-orange-700 hover:text-orange-900"
                data-testid={`naloop-plan-${n.id}`}
                onClick={() => setPickerFor(n.id)}
              >Inplannen…</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AssignmentDrawer({ assignment, onClose, onSaved, projects, vehicles, users, materials = [] }) {
  const [form, setForm] = useState({
    project_id: assignment.project_id,
    vehicle_id: assignment.vehicle_id || "",
    start_time: assignment.start_time || "07:00",
    end_time: assignment.end_time || "16:00",
    task_description: assignment.task_description || "",
    material_checklist: assignment.material_checklist || [],
    photos: [],
    office_note: assignment.office_note || "",
    status: assignment.status || "gepland",
    members: assignment.members || [],
    needs_naloop: assignment.needs_naloop || false,
    naloop_note: assignment.naloop_note || "",
  });
  const [photosLoading, setPhotosLoading] = useState(Boolean(assignment.id));

  useEffect(() => {
    if (!assignment.id) return;
    api.get(`/assignments/${assignment.id}/photos`)
      .then((r) => setForm((f) => ({ ...f, photos: r.data.photos || [] })))
      .catch((err) => console.warn("Foto's laden mislukt", err))
      .finally(() => setPhotosLoading(false));
  }, [assignment.id]);
  const [newItem, setNewItem] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const [dupDates, setDupDates] = useState([]);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [projPhotos, setProjPhotos] = useState([]);

  useEffect(() => {
    if (!form.project_id) { setProjPhotos([]); return; }
    api.get(`/projects/${form.project_id}`).then((r) => setProjPhotos(r.data.photos || [])).catch(() => setProjPhotos([]));
  }, [form.project_id]);

  const duplicateToDays = async () => {
    if (!dupDates.length) { toast.error("Kies 1+ datums"); return; }
    setDupBusy(true);
    try {
      const isoList = dupDates.map((d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
      const r = await api.post(`/assignments/${assignment.id}/duplicate`, { dates: isoList });
      const skipped = r.data.skipped_existing?.length || 0;
      if (skipped > 0) {
        toast.success(`${r.data.created} planningen aangemaakt · ${skipped} overgeslagen (stonden al gepland)`);
      } else {
        toast.success(`${r.data.created} planningen aangemaakt`);
      }
      setDupDates([]);
      setDupOpen(false);
      // Reload parent grid WITHOUT closing this drawer so planners kunnen doorwerken
      if (typeof onSaved === "function") await onSaved({ keepOpen: true });
    } catch { toast.error("Duplicatie mislukt"); }
    finally { setDupBusy(false); }
  };

  const compressImage = (file, maxW = 1600, quality = 0.72) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.photos.length + files.length > 5) {
      toast.error("Maximaal 5 foto's per dag");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const compressed = await Promise.all(files.map((f) => compressImage(f)));
      if (assignment?.id) {
        // Edit-mode: elke foto los uploaden zodat elke request onder de
        // ingress body-limit blijft (root-cause fix voor 'kan geen foto uploaden').
        let latest = form.photos;
        for (const src of compressed) {
          try {
            await api.post(`/assignments/${assignment.id}/photos`, { data_url: src });
            latest = [...latest, src];
          } catch (err) {
            const msg = err?.response?.data?.detail || "Foto uploaden mislukt";
            toast.error(msg);
            break;
          }
        }
        setForm((f) => ({ ...f, photos: latest.slice(0, 5) }));
      } else {
        setForm((f) => ({ ...f, photos: [...f.photos, ...compressed].slice(0, 5) }));
      }
    } catch { toast.error("Kon foto niet verwerken. Gebruik een JPG- of PNG-foto."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removePhoto = async (i) => {
    if (assignment?.id && form.photos[i]) {
      try {
        await api.delete(`/assignments/${assignment.id}/photos/${i}`);
      } catch { toast.error("Foto verwijderen mislukt"); return; }
    }
    setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    // Build diff patch only
    const original = {
      project_id: assignment.project_id,
      vehicle_id: assignment.vehicle_id || "",
      start_time: assignment.start_time || "07:00",
      end_time: assignment.end_time || "16:00",
      task_description: assignment.task_description || "",
      material_checklist: assignment.material_checklist || [],
      photos: [],
      office_note: assignment.office_note || "",
      status: assignment.status || "gepland",
      members: assignment.members || [],
      needs_naloop: assignment.needs_naloop || false,
      naloop_note: assignment.naloop_note || "",
    };
    const patch = {};
    for (const k of Object.keys(form)) {
      if (k === "photos") continue;
      if (JSON.stringify(form[k]) !== JSON.stringify(original[k])) patch[k] = form[k];
    }
    if (Object.keys(patch).length === 0) { toast.success("Opgeslagen"); onSaved(); return; }
    setSaving(true);
    try {
      await api.patch(`/assignments/${assignment.id}`, patch);
      toast.success("Opgeslagen");
      await onSaved();
    } catch { toast.error("Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/assignments/${assignment.id}`);
      toast.success("Verwijderd");
      await onSaved();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const setMemberRole = (uid, role) => {
    setForm((f) => ({ ...f, members: f.members.map((m) => (m.user_id === uid ? { ...m, role_today: role } : m)) }));
  };
  const removeMember = (uid) => setForm((f) => ({ ...f, members: f.members.filter((m) => m.user_id !== uid) }));
  const addMember = (uid) => {
    const u = users.find((x) => x.id === uid);
    const teammates = u?.team_id ? users.filter((x) => x.team_id === u.team_id && x.active !== false).map((x) => x.id) : [uid];
    setForm((f) => {
      const existing = new Set(f.members.map((m) => m.user_id));
      const additions = teammates.filter((tid) => !existing.has(tid)).map((tid) => ({ user_id: tid, role_today: "meerijder" }));
      return { ...f, members: [...f.members, ...additions] };
    });
  };

  const proj = projects.find((p) => p.id === form.project_id);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-slate-900/50" onClick={onClose} />
      <div className="w-full max-w-xl bg-white border-l-2 border-slate-900 h-full overflow-y-auto pb-scrollbar" data-testid="assignment-drawer">
        <div className="sticky top-0 z-10 bg-slate-900 text-white p-4 flex items-center justify-between border-b-2 border-slate-900">
          <div>
            <div className="text-xs uppercase tracking-widest text-orange-400 font-bold">Planning</div>
            <div className="pb-heading text-xl font-black">{fmtNL(assignment.date)}</div>
            {proj && <div className="text-xs text-slate-300 font-semibold">{[proj.name, proj.city].filter(Boolean).join(" · ")}</div>}
          </div>
          <button onClick={onClose} className="text-white p-1" data-testid="drawer-close"><X size={24}/></button>
        </div>
        <div className="p-4 space-y-4">
          <Field label="Project">
            <select className="pb-input" value={form.project_id} onChange={(e) => setForm({...form, project_id: e.target.value})} data-testid="drawer-project">
              {!projects.some((p) => p.id === form.project_id) && <option value={form.project_id}>— Onbekend project —</option>}
              {projects.map((p) => <option key={p.id} value={p.id}>{[p.name, p.city].filter(Boolean).join(" — ")}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starttijd">
              <input className="pb-input" type="time" value={form.start_time} onChange={(e) => setForm({...form, start_time: e.target.value})} data-testid="drawer-start"/>
            </Field>
            <Field label="Eindtijd">
              <input className="pb-input" type="time" value={form.end_time} onChange={(e) => setForm({...form, end_time: e.target.value})} data-testid="drawer-end"/>
            </Field>
          </div>
          <Field label="Voertuig">
            <select className="pb-input" value={form.vehicle_id || ""} onChange={(e) => setForm({...form, vehicle_id: e.target.value})} data-testid="drawer-vehicle">
              <option value="">— Geen voertuig —</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.license_plate})</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className="pb-input" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="drawer-status">
              {Object.entries(STATUS_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>

          {/* Naloop-werk toggle */}
          <div className="border-2 border-orange-500 rounded p-3 bg-orange-50">
            <label className="flex items-center gap-2 font-black text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.needs_naloop}
                onChange={(e) => setForm({...form, needs_naloop: e.target.checked})}
                className="w-4 h-4 accent-orange-600"
                data-testid="drawer-naloop-check"
              />
              <RefreshCcw size={14}/> Naloop-werk nodig
            </label>
            {form.needs_naloop && (
              <textarea
                className="pb-input !min-h-[60px] py-2 mt-2 text-sm"
                placeholder="Wat moet er nog gebeuren? (bv. afkitten, laatste plafondlaag)"
                value={form.naloop_note}
                onChange={(e) => setForm({...form, naloop_note: e.target.value})}
                data-testid="drawer-naloop-note"
              />
            )}
            {form.needs_naloop && (
              <div className="text-[10px] font-bold text-orange-700 mt-1 uppercase tracking-widest">Verschijnt straks in de Naloop-lijst links op het planbord</div>
            )}
          </div>

          {/* Duplicate to other days */}
          <div className="border-2 border-slate-900 rounded p-3">
            <div className="flex items-center gap-2 font-black text-sm mb-1">
              <Copy size={14}/> Kopieer deze planning naar andere dagen
            </div>
            <div className="text-[11px] text-slate-600 font-semibold mb-2">Zelfde ploeg, bus en tijd. Klik meerdere dagen in de kalender aan om ze allemaal in één keer te kopiëren.</div>
            <div className="flex gap-2 flex-wrap items-center">
              <Popover open={dupOpen} onOpenChange={setDupOpen}>
                <PopoverTrigger asChild>
                  <button className="pb-input !min-h-[40px] flex-1 min-w-[200px] flex items-center gap-2 text-left" data-testid="drawer-dup-dates">
                    <CalendarDays size={14}/>
                    {dupDates.length === 0
                      ? <span className="text-slate-500">Kies 1 of meer dagen…</span>
                      : <span className="font-bold">{dupDates.length} dag{dupDates.length === 1 ? "" : "en"} gekozen</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[60]" align="start">
                  <Calendar
                    mode="multiple"
                    selected={dupDates}
                    onSelect={(dates) => setDupDates(dates || [])}
                    disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }}
                    weekStartsOn={1}
                  />
                  <div className="p-2 border-t-2 border-slate-900 flex gap-2 justify-between text-xs font-bold">
                    <button onClick={() => setDupDates([])} className="text-slate-600 hover:text-slate-900">Wissen</button>
                    <button onClick={() => setDupOpen(false)} className="pb-btn-outline !min-h-[30px] !text-xs !px-3">Klaar</button>
                  </div>
                </PopoverContent>
              </Popover>
              <button onClick={duplicateToDays} disabled={dupBusy || dupDates.length === 0} className="pb-btn-outline !min-h-[40px] flex items-center gap-1 disabled:opacity-50" data-testid="drawer-dup-btn">
                <Copy size={14}/> {dupBusy ? "…" : "Kopieer"}
              </button>
            </div>
          </div>
          <Field label={`Ploeg (${form.members.length})`}>
            <div className="space-y-2">
              {form.members.map((m) => {
                const u = users.find((x) => x.id === m.user_id);
                return (
                  <div key={m.user_id} className="flex items-center gap-2 border-2 border-slate-900 rounded p-2">
                    <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-black">{initials(u?.name || "?")}</div>
                    <div className="flex-1">
                      <div className="font-bold text-sm">{u?.name || "?"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{u?.function}</div>
                    </div>
                    <select className="pb-input !min-h-[36px] !w-auto !py-0" value={m.role_today} onChange={(e) => setMemberRole(m.user_id, e.target.value)}>
                      <option value="chauffeur">Chauffeur</option>
                      <option value="meerijder">Meerijder</option>
                      <option value="eigen_vervoer">Eigen vervoer</option>
                    </select>
                    <button onClick={() => removeMember(m.user_id)} className="text-red-600 p-2"><Trash2 size={16}/></button>
                  </div>
                );
              })}
              <select className="pb-input" value="" onChange={(e) => { if (e.target.value) addMember(e.target.value); }} data-testid="drawer-add-member">
                <option value="">+ Voeg medewerker toe (team wordt meegenomen)</option>
                {users.filter((u) => !form.members.some((m) => m.user_id === u.id)).map((u) => (
                  <option key={u.id} value={u.id}>{u.name}{u.team_id ? " · team" : ""}</option>
                ))}
              </select>
            </div>
          </Field>
          <Field label="Taakomschrijving">
            <textarea className="pb-input min-h-[100px] py-2" value={form.task_description} onChange={(e) => setForm({...form, task_description: e.target.value})} data-testid="drawer-task"/>
          </Field>
          <Field label="Materiaal-checklist">
            <div className="space-y-1">
              {form.material_checklist.map((item, i) => (
                <div key={`${i}-${item}`} className="flex items-center gap-2">
                  <div className="flex-1 border-2 border-slate-900 rounded px-2 py-2 font-semibold text-sm">{item}</div>
                  <button onClick={() => setForm({...form, material_checklist: form.material_checklist.filter((_,idx)=>idx!==i)})} className="text-red-600 p-2"><Trash2 size={16}/></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  className="pb-input !min-h-[40px]"
                  placeholder="Kies uit lijst of typ nieuw item"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  list="mat-catalog"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newItem.trim()) { setForm({...form, material_checklist: [...form.material_checklist, newItem.trim()]}); setNewItem(""); } } }}
                  data-testid="drawer-material-input"
                />
                <button className="pb-btn-outline !min-h-[40px]" onClick={() => { if (newItem.trim()) { setForm({...form, material_checklist: [...form.material_checklist, newItem.trim()]}); setNewItem(""); } }} data-testid="drawer-material-add">Toevoegen</button>
              </div>
              <datalist id="mat-catalog">
                {materials.map((m) => (
                  <option key={m.id} value={m.name}>{m.category ? `${m.category}${m.unit ? " · " + m.unit : ""}` : (m.unit || "")}</option>
                ))}
              </datalist>
              {materials.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Snel toevoegen uit catalogus</div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto pb-scrollbar">
                    {materials.filter((m) => !form.material_checklist.includes(m.name)).slice(0, 30).map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setForm({...form, material_checklist: [...form.material_checklist, m.name]})}
                        className="text-xs font-bold border-2 border-slate-900 rounded px-2 py-1 hover:bg-orange-100"
                        data-testid={`drawer-material-chip-${m.id}`}
                      >
                        + {m.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Field>
          <Field label="Notitie voor de ploeg">
            <textarea className="pb-input min-h-[80px] py-2" value={form.office_note} onChange={(e) => setForm({...form, office_note: e.target.value})}/>
          </Field>

          <Field label={photosLoading ? `Foto's (laden…)` : `Foto's (dagfoto's · ${form.photos.length}/5)`}>
            <div className="space-y-2">
              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {form.photos.map((src, i) => (
                    <div key={`${i}-${src.length}-${src.slice(-40)}`} className="relative border-2 border-slate-900 rounded overflow-hidden aspect-square" data-testid={`drawer-photo-${i}`}>
                      <img src={src} alt="" className="w-full h-full object-cover"/>
                      <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 border-2 border-white shadow-md hover:bg-red-700" data-testid={`drawer-photo-remove-${i}`} aria-label="Foto verwijderen">
                        <X size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {form.photos.length < 5 && (
                <>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploading}
                    className="pb-btn-outline flex items-center justify-center gap-2 !min-h-[44px] w-full disabled:opacity-60"
                    data-testid="drawer-photo-upload"
                  >
                    <Camera size={18}/> {uploading ? "Bezig…" : "Foto's toevoegen (van schouwer)"}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onPickPhotos}
                    disabled={uploading}
                  />
                </>
              )}
              <div className="text-[11px] font-medium text-slate-500">De uitvoerder ziet deze foto&apos;s op zijn Vandaag-scherm.</div>
            </div>
          </Field>

          {projPhotos.length > 0 && (
            <Field label={`Projectfoto's (${projPhotos.length})`}>
              <div className="grid grid-cols-3 gap-2" data-testid="drawer-project-photos">
                {projPhotos.map((src, i) => (
                  <button type="button" key={`${i}-${src.length}-${src.slice(-40)}`} onClick={() => setLightbox(src)} className="block border-2 border-slate-300 rounded overflow-hidden aspect-square hover:border-orange-600" data-testid={`drawer-project-photo-${i}`}>
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
              <div className="text-[11px] font-medium text-slate-500 mt-1">Bijgevoegd bij projectaanmaak. Klik op een foto om te vergroten.</div>
            </Field>
          )}

          {form.photos.length > 0 && (
            <Field label="Dagfoto's">
              <div className="grid grid-cols-3 gap-2">
                {form.photos.map((src, i) => (
                  <button type="button" key={`day-${i}`} onClick={() => setLightbox(src)} className="block border-2 border-slate-300 rounded overflow-hidden aspect-square hover:border-orange-600" data-testid={`drawer-day-photo-${i}`}>
                    <img src={src} alt="" loading="lazy" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            </Field>
          )}

          <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-4">
            <button className="pb-btn-primary flex-1" onClick={save} disabled={saving} data-testid="drawer-save">{saving ? "Opslaan…" : "Opslaan"}</button>
            <button className="pb-btn-outline !bg-red-50 !border-red-600 !text-red-700" onClick={() => setConfirmDelete(true)} data-testid="drawer-delete-open"><Trash2 size={18}/></button>
          </div>
        </div>

        {confirmDelete && (
          <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4">
            <div className="pb-card w-full max-w-sm p-5" data-testid="confirm-delete">
              <div className="pb-heading text-lg font-black mb-2">Deze planning verwijderen?</div>
              <div className="text-sm text-slate-700 font-medium mb-4">Dit kan niet ongedaan gemaakt worden.</div>
              <div className="flex gap-2">
                <button className="pb-btn-outline flex-1" onClick={() => setConfirmDelete(false)} data-testid="confirm-cancel">Annuleren</button>
                <button className="pb-btn-primary !bg-red-600 flex-1" onClick={doDelete} data-testid="confirm-delete-ok">Verwijderen</button>
              </div>
            </div>
          </div>
        )}
        {lightbox && (
          <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="drawer-lightbox">
            <button className="absolute top-3 right-3 text-white bg-slate-900/70 rounded-full p-2 border-2 border-white" onClick={() => setLightbox(null)}><X size={24}/></button>
            <img src={lightbox} alt="" className="max-w-full max-h-[90vh] object-contain rounded"/>
          </div>
        )}
      </div>
    </div>
  );
}

function AddProjectMenu({ anchor, projects, onClose, onPick, onImportNotion }) {
  const [q, setQ] = useState("");
  // Positie binnen viewport houden
  const style = React.useMemo(() => {
    const w = 320, h = 420;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    return {
      left: Math.min(anchor.x, vw - w - 16),
      top: Math.min(anchor.y, vh - h - 16),
      width: w,
      maxHeight: h,
    };
  }, [anchor]);
  const filtered = React.useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return projects.slice(0, 50);
    return projects.filter((p) =>
      (p.name || "").toLowerCase().includes(ql)
      || (p.city || "").toLowerCase().includes(ql)
      || (p.address || "").toLowerCase().includes(ql)
    ).slice(0, 50);
  }, [projects, q]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[80]" onClick={onClose} data-testid="add-project-menu">
      <div
        className="absolute pb-card bg-white border-2 border-slate-900 shadow-xl overflow-hidden flex flex-col"
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-slate-900 text-white px-3 py-2 flex items-center justify-between">
          <div className="text-xs font-black uppercase tracking-widest">Project toevoegen</div>
          <button onClick={onClose} className="text-white" data-testid="add-menu-close"><X size={16}/></button>
        </div>
        <div className="p-2 border-b-2 border-slate-200">
          <input
            autoFocus
            className="pb-input !min-h-[36px] !text-sm !py-1"
            placeholder="Zoek project…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="add-menu-search"
          />
        </div>
        <div className="flex-1 overflow-y-auto pb-scrollbar p-1">
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 italic text-center py-6">Niks gevonden</div>
          ) : filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p.id)}
              className="w-full text-left p-2 rounded hover:bg-orange-50 flex items-center gap-2"
              data-testid={`add-menu-pick-${p.id}`}
            >
              <span className="w-3 h-3 rounded-sm border border-slate-900 shrink-0" style={{ background: p.color || "#3B82F6" }}/>
              <div className="min-w-0">
                <div className="font-black text-sm truncate">{p.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{p.city || "—"}</div>
              </div>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onImportNotion}
          className="border-t-2 border-slate-200 bg-slate-50 hover:bg-orange-50 px-3 py-2 text-sm font-bold text-orange-700 flex items-center gap-2"
          data-testid="add-menu-notion"
        >
          <span>➕ Importeer uit Notion</span>
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-slate-700">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function getWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(),0,1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}
